<script setup>
import TvScorebug from '~/components/tv/TvScorebug.vue';
import {TV_COMPETITION_ICON, tvAgo} from '~/utils/tvDisplay';

// The AI write-ups of the latest confirmed results, one per page across the
// whole feature area: the match as a large scorebug the full width, then the
// report beneath it as commentary (a microphone, the words in italics, a credit).
// Only sent for full-names screens and matches without an under-18.
const props = defineProps({
  panel: {type: Object, required: true},
  page: {type: Number, default: 0},
});

const item = computed(() => (props.panel.reports ?? []).slice(0, 3)[props.page] ?? null);
</script>

<template>
  <article v-if="item" class="tv-report">
    <div class="tv-report-match">
      <div class="tv-report-meta">
        <v-icon :icon="TV_COMPETITION_ICON[item.result.competitionKind]" size="24"/>
        <span class="tv-report-comp">{{ item.result.competitionName }}</span>
        <span class="tv-report-when">Played {{ tvAgo(item.result.playedAt) }}</span>
      </div>
      <tv-scorebug :result="item.result" size="list" class="tv-report-bug"/>
    </div>

    <blockquote class="tv-report-quote">
      <span class="tv-report-mic"><v-icon icon="mdi-microphone" size="30"/></span>
      <p class="tv-report-text">{{ item.report }}</p>
      <footer class="tv-report-credit">Match commentary by Tap Leagues</footer>
    </blockquote>
  </article>
</template>

<style scoped lang="scss">
.tv-report {
  display: flex;
  flex-direction: column;
  gap: 32px;
  width: 100%;
  padding-top: 4px;
}

.tv-report-meta {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
  font-size: 22px;
  color: var(--tv-muted);
}

.tv-report-comp {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* The match is the headline: a large scorebug filling its column */
.tv-report-bug {
  width: 100%;
  --bug-row: 64px;
  --bug-font: 30px;
  --bug-score: 36px;
  --bug-cell: 62px;
  --bug-score-cell: 76px;
}

.tv-report-meta .tv-report-when {
  margin-left: auto;
  white-space: nowrap;
}

.tv-report-when {
  font-size: 20px;
  color: var(--tv-muted);
}

/* The report as commentary */
.tv-report-quote {
  position: relative;
  margin: 0;
  padding: 0 0 0 36px;
  border-left: 4px solid var(--tv-accent);
}

.tv-report-mic {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  margin-bottom: 18px;
  border-radius: 50%;
  background: var(--tv-accent-tint);
  color: var(--tv-accent-text);
}

.tv-report-text {
  margin: 0;
  font-size: 30px;
  font-style: italic;
  line-height: 1.55;
  color: var(--tv-ink);
}

.tv-report-text::before {
  content: '\201C';
  margin-right: 4px;
  font-family: Georgia, serif;
  font-size: 1.6em;
  line-height: 0;
  vertical-align: -0.28em;
  color: var(--tv-accent-text);
}

.tv-report-credit {
  margin-top: 22px;
  font-size: 19px;
  color: var(--tv-muted);
}

:global(.tv-narrow .tv-report-text) {
  font-size: 26px;
}
</style>
